/**
 * Session context: the signed-in user, their effective permissions, and the
 * active locale.
 *
 * Permissions are read live from `userEffectivePermissions/{uid}` rather than
 * from the ID token, so an administrator revoking a role takes effect in open
 * tabs immediately. The UI uses them to decide what to render; the server
 * checks them again on every call, because a hidden button is not a control.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { Timestamp, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "./firebase.ts";
import { translate, type Locale, type StringKey } from "./i18n.ts";
import { permissionsForRole } from "./roles.ts";
import type { DocumentData } from "firebase/firestore";

const CAMPAIGN_ORGANIZATION_ID = "canales-2026";
const DEMO_ORGANIZATION_ID = "canales-demo";

export interface Profile {
  uid: string;
  email: string | null;
  displayName: string;
  title: string | null;
  roles: string[];
  locale: Locale;
  organizationId: string;
  status: "active" | "suspended";
  demoMode?: boolean;
  /** Set for username accounts; they sign in without an email. */
  username?: string;
  /** Set by provisioning: the temporary password must be replaced first. */
  mustChangePassword?: boolean;
  /** Set by provisioning: show the welcome popup and offer the tour. */
  welcomePending?: boolean;
  /** Email-notification preferences; written only by setNotificationPreferences. */
  notifications?: DocumentData;
}

export interface CampaignLicense {
  status: "active" | "payment_required" | "awaiting_base" | "refunded";
  basePaid: boolean;
  runoffPaid: boolean;
  accessUntil: Timestamp | null;
  /** Stripe subscription state: active, past_due, canceled, … (null before checkout). */
  subscriptionStatus: string | null;
}

/** Prepaid AI credit (1_000_000 micros = $1). Written only by server functions. */
export interface CampaignWallet {
  balanceMicros: number;
  reservedMicros: number;
}

export interface UserTrial {
  status: "active" | "expired" | "exhausted";
  startsAt: Timestamp;
  endsAt: Timestamp;
  spendLimitMicros: number;
  spentMicros: number;
  reservedMicros: number;
}

interface SessionValue {
  firebaseUser: User | null;
  profile: Profile | null;
  /** Effective permissions the UI renders against — the simulated role's when
   *  previewing, otherwise the real ones. */
  permissions: string[];
  /** The account's own permissions, never overridden by preview. Gate the
   *  preview control itself on these so it can't hide itself. */
  realPermissions: string[];
  loading: boolean;
  /** True when signed in but no users/{uid} record exists. */
  missingProfile: boolean;
  /** True when a valid Firebase account belongs to another campaign. */
  campaignMismatch: boolean;
  license: CampaignLicense | null;
  licenseResolved: boolean;
  licensed: boolean;
  trial: UserTrial | null;
  trialResolved: boolean;
  trialActive: boolean;
  /** Null until the campaign has ever bought credit. */
  wallet: CampaignWallet | null;
  developerBypass: boolean;
  /** Shared, isolated, rules-enforced read-only product demonstration. */
  demoMode: boolean;
  /** The role currently being previewed via "View as", or null. */
  viewAsRole: string | null;
  /** Set/clear the previewed role. No-op unless the account holds "*". */
  setViewAsRole: (role: string | null) => void;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
  can: (permission: string) => boolean;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [authResolved, setAuthResolved] = useState(false);
  const [profileResolved, setProfileResolved] = useState(false);
  const [license, setLicense] = useState<CampaignLicense | null>(null);
  const [licenseResolved, setLicenseResolved] = useState(false);
  const [trial, setTrial] = useState<UserTrial | null>(null);
  const [trialResolved, setTrialResolved] = useState(false);
  const [trialNow, setTrialNow] = useState(() => Date.now());
  const [wallet, setWallet] = useState<CampaignWallet | null>(null);
  const [localeOverride, setLocaleOverride] = useState<Locale | null>(null);
  const [viewAsRole, setViewAsRoleState] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setAuthResolved(true);
      if (!user) {
        setProfile(null);
        setPermissions([]);
        setProfileResolved(true);
        setViewAsRoleState(null);
        setLicense(null);
        setLicenseResolved(true);
        setTrial(null);
        setTrialResolved(true);
      } else {
        setProfileResolved(false);
        setLicenseResolved(false);
        setTrialResolved(false);
      }
    });
  }, []);

  useEffect(() => {
    if (!firebaseUser) return;
    let sawProfile = false;

    const unsubProfile = onSnapshot(
      doc(db, "users", firebaseUser.uid),
      (snap) => {
        sawProfile = snap.exists();
        setProfile(
          snap.exists()
            ? ({ uid: snap.id, ...snap.data() } as Profile)
            : null,
        );
        setProfileResolved(true);
      },
      () => {
        // A permission error here means no readable user record.
        setProfile(null);
        setProfileResolved(true);
      },
    );

    const unsubPermissions = onSnapshot(
      doc(db, "userEffectivePermissions", firebaseUser.uid),
      (snap) => setPermissions(snap.exists() ? (snap.get("permissions") ?? []) : []),
      () => setPermissions([]),
    );

    return () => {
      unsubProfile();
      unsubPermissions();
      void sawProfile;
    };
  }, [firebaseUser]);

  useEffect(() => {
    if (!firebaseUser) return;
    return onSnapshot(
      doc(db, "userTrials", firebaseUser.uid),
      (snap) => {
        setTrial(snap.exists() ? (snap.data() as UserTrial) : null);
        setTrialResolved(true);
      },
      () => {
        setTrial(null);
        setTrialResolved(true);
      },
    );
  }, [firebaseUser]);

  useEffect(() => {
    if (!trial) return;
    const timer = window.setInterval(() => setTrialNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [trial]);

  useEffect(() => {
    if (!firebaseUser || !profileResolved) return;
    if (
      profile?.organizationId === DEMO_ORGANIZATION_ID &&
      profile.demoMode === true &&
      profile.roles?.includes("demo_viewer")
    ) {
      setLicense(null);
      setLicenseResolved(true);
      return;
    }
    if (profile?.organizationId !== CAMPAIGN_ORGANIZATION_ID) {
      setLicense(null);
      setLicenseResolved(true);
      return;
    }
    return onSnapshot(
      doc(db, "campaignBilling", CAMPAIGN_ORGANIZATION_ID),
      (snap) => {
        if (!snap.exists()) {
          setLicense(null);
        } else {
          const accessUntil = snap.get("accessUntil");
          setLicense({
            status: snap.get("status") ?? "payment_required",
            basePaid: snap.get("basePaid") === true,
            runoffPaid: snap.get("runoffPaid") === true,
            accessUntil: accessUntil instanceof Timestamp ? accessUntil : null,
            subscriptionStatus: snap.get("subscriptionStatus") ?? null,
          });
        }
        setLicenseResolved(true);
      },
      () => {
        setLicense(null);
        setLicenseResolved(true);
      },
    );
  }, [firebaseUser, profileResolved, profile]);

  useEffect(() => {
    if (!firebaseUser || profile?.organizationId !== CAMPAIGN_ORGANIZATION_ID) {
      setWallet(null);
      return;
    }
    return onSnapshot(
      doc(db, "campaignWallets", CAMPAIGN_ORGANIZATION_ID),
      (snap) => {
        setWallet(
          snap.exists()
            ? {
                balanceMicros: Number(snap.get("balanceMicros") ?? 0),
                reservedMicros: Number(snap.get("reservedMicros") ?? 0),
              }
            : null,
        );
      },
      () => setWallet(null),
    );
  }, [firebaseUser, profile]);

  // English is this campaign's default; anyone can switch with the EN/ES toggle.
  const locale: Locale = localeOverride ?? profile?.locale ?? "en";

  // Only an account holding the "*" wildcard may preview another role.
  // Everyone else's setter is inert, and the effective
  // permissions collapse to the simulated role's — nav and page gates then
  // render exactly as that role would see them.
  const isPrivileged = permissions.includes("*");
  const setViewAsRole = useCallback(
    (role: string | null) => setViewAsRoleState(role),
    [],
  );
  const effectivePermissions =
    isPrivileged && viewAsRole ? permissionsForRole(viewAsRole) : permissions;
  const demoMode = Boolean(
    profile?.organizationId === DEMO_ORGANIZATION_ID &&
      profile.demoMode === true &&
      profile.roles?.includes("demo_viewer"),
  );
  const campaignMismatch = Boolean(
    profile && profile.organizationId !== CAMPAIGN_ORGANIZATION_ID && !demoMode,
  );
  const developerBypass = profile?.roles?.includes("developer") === true;
  const licensed = Boolean(
    license?.status === "active" &&
      license.accessUntil &&
      license.accessUntil.toMillis() > Date.now(),
  );
  const trialActive = Boolean(
    trial?.status === "active" &&
      trial.endsAt instanceof Timestamp &&
      trial.endsAt.toMillis() > trialNow &&
      trial.spentMicros < trial.spendLimitMicros,
  );

  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleOverride(next);
      // Persist so the choice follows the user to their next device, and so
      // the advisor answers in the same language they read the UI in.
      if (firebaseUser && !demoMode) {
        void updateDoc(doc(db, "users", firebaseUser.uid), { locale: next }).catch(
          () => {
            /* Non-fatal: the override still applies for this session. */
          },
        );
      }
    },
    [firebaseUser, demoMode],
  );

  const value = useMemo<SessionValue>(
    () => ({
      firebaseUser,
      profile,
      permissions: effectivePermissions,
      realPermissions: permissions,
      loading: !authResolved || (firebaseUser !== null && !profileResolved),
      missingProfile: Boolean(firebaseUser) && profileResolved && profile === null,
      campaignMismatch,
      license,
      licenseResolved,
      licensed,
      trial,
      trialResolved,
      trialActive,
      wallet,
      developerBypass,
      demoMode,
      viewAsRole: isPrivileged ? viewAsRole : null,
      setViewAsRole,
      locale,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
      can: (permission) =>
        effectivePermissions.includes("*") ||
        effectivePermissions.includes(permission),
      logout: async () => {
        if (demoMode) sessionStorage.removeItem("canales-demo-tour-seen");
        setLocaleOverride(null);
        setViewAsRoleState(null);
        await signOut(auth);
      },
    }),
    [
      firebaseUser,
      profile,
      permissions,
      effectivePermissions,
      isPrivileged,
      viewAsRole,
      setViewAsRole,
      authResolved,
      profileResolved,
      campaignMismatch,
      license,
      licenseResolved,
      licensed,
      trial,
      trialResolved,
      trialActive,
      wallet,
      developerBypass,
      demoMode,
      locale,
      setLocale,
    ],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used inside a SessionProvider.");
  }
  return context;
}
